
import Vue from 'vue';
import { Component, Prop, Watch } from 'vue-property-decorator';
import { namespace } from 'vuex-class';
import { today, sixMonthsAgo } from '@util/dates-and-times';
import BaseModal from '@components/new-styleguide/BaseModal.vue';
import IconOeamtcClose from '@/components/icons/icon-oeamtc-close.svg';
import IconCheck from '@components/icons/icon-check.svg';
import ExportTab, { ExportModel } from '@components/my-trips/trips-overview/export-import-modal/ExportTab.vue';
import ImportTab, { ImportModel } from '@components/my-trips/trips-overview/export-import-modal/ImportTab.vue';
import { ExportFormat, TripLog } from '@/generated-clients/myTrips';
import { FilterOptions, LoadState } from '@/utils/types';
import { differenceInYears } from 'date-fns';

type Tab = 'export' | 'import';

const TriplogModule = namespace('Triplog');

@Component({
  name: 'ExportImportModal',
  components: {
    IconOeamtcClose,
    IconCheck,
    BaseModal,
    ExportTab,
    ImportTab
  }
})
export default class FilterModal extends Vue {
  @TriplogModule.Getter('getSelectedTriplog')
  private triplog: TripLog;

  @TriplogModule.Getter('getFilterOptions')
  private filterOptions: FilterOptions;

  @TriplogModule.Getter('getExportTripsStatus')
  private exportTripsStatus;

  @TriplogModule.Getter('getTripErrors')
  private tripErrors;

  @TriplogModule.Getter('getImportTripsStatus')
  private importTripsStatus;

  @TriplogModule.Action('exportTrips')
  private exportTripsRequest;

  @TriplogModule.Action('importTrips')
  private importTripsRequest;

  @TriplogModule.Mutation('RESET_EXPORT_TRIP')
  private resetExportTripStatus;

  @TriplogModule.Mutation('RESET_IMPORT_TRIP')
  private resetImportTripStatus;

  @Prop({ default: false })
  private open: boolean;

  private activeTab: Tab = 'export';
  private submitting = false;
  private exportModel: ExportModel = { date: { from: sixMonthsAgo(), until: today()}, email: '', exportFormat: ExportFormat.Pdf };
  private importModel: ImportModel = { email: '', csv: '' };

  @Watch('open')
  private onOpen(newOpen) {
    if (newOpen) {
      this.activeTab = 'export';

      this.exportModel = { date: { from: sixMonthsAgo(), until: today()}, email: '', exportFormat: ExportFormat.Pdf };
      this.importModel = { email: '', csv: '' };

      this.resetExportTripStatus();
      this.resetImportTripStatus();
    }
  }

  private close() {
    this.$emit('close');
  }

  private clickTab(tab: Tab) {
    this.activeTab = tab;
  }

  private submit() {
    if (this.exportActive) {
      this.exportTrips();
    }

    if (this.importActive) {
      this.importTrips();
    }
  }

  private async exportTrips() {
    const exportRequest = {
      vehicleId: this.triplog.vehicle.id,
      email: this.exportModel.email,
      exportFormats: [this.exportModel.exportFormat],
      from: this.exportModel.date.from,
      until: this.exportModel.date.until,
      tagIds: this.filterOptions.tagId ? [this.filterOptions.tagId] : undefined,
      pilotIds: this.filterOptions.pilotId ? [this.filterOptions.pilotId] : undefined
    }

    this.submitting = true;

    try {
      await this.exportTripsRequest(exportRequest);
    } catch(e) {
      console.error(e);
    } finally {
      // simulate loading since export requests returns almost instantly
      setTimeout(() => { this.submitting = false }, 2000);
    }
  }

  private async importTrips() {
    this.submitting = true;

    try {
      await this.importTripsRequest({ csv: this.importModel.csv, email: this.importModel.email });
    } catch(e) {
      console.error(e);
    } finally {
      this.submitting = false;
    }
  }

  get exportActive() {
    return this.activeTab === 'export';
  }

  get importActive() {
    return this.activeTab === 'import';
  }

  get submitDisabled() {
    if (this.submitting) {
      return true;
    }

    if (this.exportActive) {
      const { email, date } = this.exportModel;
      const years = differenceInYears(new Date(date.until), new Date(date.from));
      return !email || !this.validateEmail(email) || years >= 1;
    }

    if (this.importActive) {
      return !this.importModel.csv || !this.importModel.email;
    }
  }

  get exportSuccess() {
    return this.exportTripsStatus === LoadState.Success;
  }

  get exportError() {
    return this.exportTripsStatus === LoadState.Error;
  }

  get importSuccess() {
    return this.importTripsStatus === LoadState.Success;
  }

  get importError() {
    return this.importTripsStatus === LoadState.Error;
  }

  private validateEmail(email) {
    const re = /\S+@\S+\.\S+/;
    return re.test(String(email).toLowerCase());
  }
}
